import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Download,
  Search,
  CalendarIcon,
  Filter,
  BarChart3,
  TrendingUp,
  DollarSign,
  Activity,
  FileText,
  PieChart,
  LineChart,
  Users,
  Bot,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Eye,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  getAnalyticsData,
  generateReport,
  listReports,
  getReportById,
  downloadReport,
  type AnalyticsData as AnalyticsDataType,
  type Report as ReportType,
  type ReportDetail,
} from "@/services/reportsService";
import { getAccessibleClients } from "@/services/clientsService";
import { listAIModels, type AIModel } from "@/services/aiModelsService";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from "recharts";

interface ReportsAnalyticsProps {
  onBack: () => void;
}

type ReportData = ReportType;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

export const ReportsAnalytics = ({ onBack }: ReportsAnalyticsProps) => {
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState<"1h" | "24h" | "7d" | "30d">("7d");
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedModel, setSelectedModel] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportDetail | null>(null);
  const [reportType, setReportType] = useState<
    "api_calls" | "costs" | "tokens" | "performance" | "users" | "models"
  >("api_calls");
  const [reportFormat, setReportFormat] = useState<"pdf" | "json">("pdf");
  const [analyticsData, setAnalyticsData] = useState<AnalyticsDataType | null>(null);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [clients, setClients] = useState<Array<{ id: string; name: string; company: string }>>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const { isAdminOrSupport } = usePermissions();

  // Carregar clientes acessíveis e modelos
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [clientsRes, modelsRes] = await Promise.all([
          getAccessibleClients(),
          listAIModels({ limit: 100, is_active: true }),
        ]);

        if (clientsRes.success && clientsRes.data) {
          const clientsArray = clientsRes.data.clients || [];
          const nextClients = Array.isArray(clientsArray) ? clientsArray : [];
          setClients(nextClients);
          if (!isAdminOrSupport && nextClients.length > 0) {
            setSelectedClient(nextClients[0].id);
          }
        }

        if (modelsRes.success && modelsRes.data) {
          const modelsArray = modelsRes.data.data || [];
          setModels(Array.isArray(modelsArray) ? modelsArray : []);
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar dados",
          description: error instanceof Error ? error.message : "Erro desconhecido",
          variant: "destructive",
        });
      }
    };

    loadInitialData();
  }, [toast, isAdminOrSupport]);

  // Função para calcular a janela do período
  const getPeriodWindow = (period: "1h" | "24h" | "7d" | "30d") => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (period) {
      case "1h": {
        // Hora atual - janela de 1 hora (ex: 13:03 -> 13:00 até 13:59)
        startDate = new Date(now);
        startDate.setMinutes(0, 0, 0);
        endDate = new Date(startDate);
        endDate.setMinutes(59, 59, 999);
        break;
      }
      case "24h": {
        // Dia atual - 00:00 até 23:59
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case "7d": {
        // Semana completa - domingo até sábado (7 dias)
        const dayOfWeek = now.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
        const daysToSunday = dayOfWeek === 0 ? 0 : dayOfWeek;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToSunday);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case "30d": {
        // Mês atual - primeiro dia até último dia do mês
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
    }

    return { startDate, endDate, now };
  };

  // Função para formatar o cabeçalho do período
  const getPeriodHeader = (period: "1h" | "24h" | "7d" | "30d") => {
    // Usar data real dos dados se disponível, senão usar data solicitada
    let startDate: Date;
    let endDate: Date;
    let now: Date = new Date();

    if (analyticsData?.actualPeriod) {
      startDate = new Date(analyticsData.actualPeriod.startDate);
      endDate = new Date(analyticsData.actualPeriod.endDate);
      // Usar a data de fim como referência para "now"
      now = new Date(analyticsData.actualPeriod.endDate);
    } else {
      const periodWindow = getPeriodWindow(period);
      startDate = periodWindow.startDate;
      endDate = periodWindow.endDate;
      now = periodWindow.now;
    }

    switch (period) {
      case "1h": {
        const currentTime = format(now, "HH:mm");
        const startTime = format(startDate, "HH:mm");
        const endTime = format(endDate, "HH:mm");
        return `${currentTime} - janela: ${startTime} até ${endTime}`;
      }
      case "24h": {
        const currentDateTime = format(now, "dd/MM/yyyy HH:mm");
        const startDateTime = format(startDate, "dd/MM/yyyy HH:mm");
        const endDateTime = format(endDate, "dd/MM/yyyy HH:mm");
        return `${currentDateTime} - janela: ${startDateTime} até ${endDateTime}`;
      }
      case "7d": {
        const currentDateTime = format(now, "dd/MM/yyyy HH:mm");
        const startDateTime = format(startDate, "dd/MM/yyyy HH:mm");
        const endDateTime = format(endDate, "dd/MM/yyyy HH:mm");
        return `${currentDateTime} - janela 7 dias, iniciando no domingo ${startDateTime} até ${endDateTime}`;
      }
      case "30d": {
        const currentDateTime = format(now, "dd/MM/yyyy HH:mm");
        const startDateTime = format(startDate, "dd/MM/yyyy HH:mm");
        const endDateTime = format(endDate, "dd/MM/yyyy HH:mm");
        return `${currentDateTime} - janela 30 dias (mês), iniciando no dia ${startDateTime} até ${endDateTime}`;
      }
    }
  };

  // Carregar dados de analytics quando os filtros mudarem
  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const options: any = {
          period: selectedPeriod,
          client_id: selectedClient === "all" ? "all" : selectedClient === "null" ? null : selectedClient,
          model_id: selectedModel === "all" ? null : selectedModel,
        };

        const response = await getAnalyticsData(options);
        if (response.success && response.data) {
          setAnalyticsData(response.data);
        } else {
          toast({
            title: "Erro ao carregar analytics",
            description: response.error?.message || "Erro desconhecido",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar analytics",
          description: error instanceof Error ? error.message : "Erro desconhecido",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [selectedPeriod, selectedClient, selectedModel, toast]);

  // Carregar relatórios
  useEffect(() => {
    const loadReports = async () => {
      setLoadingReports(true);
      try {
        const response = await listReports({ limit: 50 });
        if (response.success && response.data) {
          setReports(response.data.reports);
        } else {
          toast({
            title: "Erro ao carregar relatórios",
            description: response.error?.message || "Erro desconhecido",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar relatórios",
          description: error instanceof Error ? error.message : "Erro desconhecido",
          variant: "destructive",
        });
      } finally {
        setLoadingReports(false);
      }
    };

    loadReports();
  }, [toast]);

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const request: any = {
        type: reportType,
        period: selectedPeriod,
        client_id: selectedClient === "all" ? "all" : selectedClient === "null" ? null : selectedClient,
        model_id: selectedModel === "all" ? null : selectedModel,
        format: reportFormat,
      };

      const response = await generateReport(request);
      if (response.success && response.data) {
        toast({
          title: "Relatório gerado",
          description: "O relatório está sendo processado",
        });
        setIsGenerateModalOpen(false);
        // Recarregar lista de relatórios
        const reportsResponse = await listReports({ limit: 50 });
        if (reportsResponse.success && reportsResponse.data) {
          setReports(reportsResponse.data.reports);
        }
      } else {
        toast({
          title: "Erro ao gerar relatório",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao gerar relatório",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleViewReport = async (report: ReportData) => {
    try {
      const response = await getReportById(report.id);
      if (response.success && response.data) {
        setSelectedReport(response.data);
        setIsViewModalOpen(true);
      } else {
        toast({
          title: "Erro ao carregar relatório",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar relatório",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async (report: ReportData) => {
    try {
      // Usar o serviço de token para obter o token dos cookies
      const { getToken } = await import("@/services/tokenService");
      const token = getToken();

      if (!token) {
        toast({
          title: "Erro",
          description: "Token de autenticação não encontrado. Por favor, faça login novamente.",
          variant: "destructive",
        });
        return;
      }

      // Usar a configuração centralizada da API
      const { getApiBaseUrl } = await import("@/lib/apiConfig");
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/reports/${report.id}/download`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: "Erro ao fazer download" } }));
        throw new Error(errorData.error?.message || "Erro ao fazer download");
      }

      // Verificar se é PDF pelo Content-Type
      const contentType = response.headers.get("content-type");
      if (contentType === "application/pdf") {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${report.name || `relatorio_${report.id}`}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast({
          title: "Download iniciado",
          description: "O relatório PDF foi baixado com sucesso",
        });
      } else {
        // JSON ou outro formato
        const data = await response.json();
        if (data.success && data.data) {
          const dataStr = JSON.stringify(data.data.data, null, 2);
          const blob = new Blob([dataStr], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${report.name || `relatorio_${report.id}`}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast({
            title: "Download iniciado",
            description: "O relatório foi baixado com sucesso",
          });
        } else {
          throw new Error(data.error?.message || "Erro ao fazer download");
        }
      }
    } catch (error) {
      toast({
        title: "Erro ao fazer download",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const filteredReports = reports.filter((report) =>
    report.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        title="Relatórios e Análises"
        description="Análise detalhada de dados e geração de relatórios"
        onBack={onBack}
      />

      {/* Main Content */}
      <main className="container mx-auto p-6 space-y-8">
        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros de Análise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label>Período</Label>
                <Select
                  value={selectedPeriod}
                  onValueChange={setSelectedPeriod}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">Última Hora</SelectItem>
                    <SelectItem value="24h">Últimas 24h</SelectItem>
                    <SelectItem value="7d">Últimos 7 dias</SelectItem>
                    <SelectItem value="30d">Últimos 30 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isAdminOrSupport && (
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select
                  value={selectedClient}
                  onValueChange={setSelectedClient}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Clientes</SelectItem>
                    <SelectItem value="null">Sandbox</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company ? `(${client.company})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              <div className="space-y-2">
                <Label>Modelo de IA</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Modelos</SelectItem>
                    {models.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ações</Label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setIsGenerateModalOpen(true)}
                    disabled={isGeneratingReport}
                  >
                    {isGeneratingReport ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    Gerar Relatório
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Overview */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total de Chamadas
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-2xl font-bold">Carregando...</div>
              ) : analyticsData ? (
                <>
                  <div className="text-2xl font-bold">
                    {analyticsData.kpis.totalCalls.value.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analyticsData.kpis.totalCalls.variation >= 0 ? "+" : ""}
                    {analyticsData.kpis.totalCalls.variation.toFixed(1)}% em relação ao período anterior
                  </p>
                </>
              ) : (
                <div className="text-2xl font-bold">-</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-2xl font-bold">Carregando...</div>
              ) : analyticsData ? (
                <>
                  <div className="text-2xl font-bold">
                    ${analyticsData.kpis.totalCost.value.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analyticsData.kpis.totalCost.variation >= 0 ? "+" : ""}
                    {analyticsData.kpis.totalCost.variation.toFixed(1)}% em relação ao período anterior
                  </p>
                </>
              ) : (
                <div className="text-2xl font-bold">-</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Taxa de Sucesso
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-2xl font-bold">Carregando...</div>
              ) : analyticsData ? (
                <>
                  <div className="text-2xl font-bold">
                    {analyticsData.kpis.successRate.value.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analyticsData.kpis.successRate.variation >= 0 ? "+" : ""}
                    {analyticsData.kpis.successRate.variation.toFixed(1)}% em relação ao período anterior
                  </p>
                </>
              ) : (
                <div className="text-2xl font-bold">-</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-2xl font-bold">Carregando...</div>
              ) : analyticsData ? (
                <>
                  <div className="text-2xl font-bold">
                    {analyticsData.kpis.avgResponseTime.value.toFixed(1)}s
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analyticsData.kpis.avgResponseTime.variation >= 0 ? "+" : ""}
                    {analyticsData.kpis.avgResponseTime.variation.toFixed(1)}s em relação ao período anterior
                  </p>
                </>
              ) : (
                <div className="text-2xl font-bold">-</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="trends" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="trends">Tendências</TabsTrigger>
            <TabsTrigger value="models">Modelos</TabsTrigger>
            <TabsTrigger value="clients">Clientes</TabsTrigger>
            <TabsTrigger value="distribution">Distribuição</TabsTrigger>
          </TabsList>

          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tendência Diária</CardTitle>
                <CardDescription>
                  {getPeriodHeader(selectedPeriod)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={analyticsData?.dailyTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        angle={selectedPeriod === "30d" ? -45 : 0}
                        textAnchor={selectedPeriod === "30d" ? "end" : "middle"}
                        height={selectedPeriod === "30d" ? 80 : 30}
                      />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="calls"
                        fill="#8884d8"
                        name="Chamadas"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cost"
                        stroke="#82ca9d"
                        name="Custo ($)"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="models" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Top Modelos por Chamadas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analyticsData?.topModels || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="calls" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Distribuição de Custos por Modelo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={analyticsData?.topModels || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="cost"
                        >
                          {(analyticsData?.topModels || []).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="clients" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Top Clientes por Uso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData?.topClients || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="calls"
                        fill="#8884d8"
                        name="Chamadas"
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="cost"
                        fill="#82ca9d"
                        name="Custo ($)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="distribution" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribuição Horária</CardTitle>
                <CardDescription>
                  {getPeriodHeader(selectedPeriod)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData?.hourlyDistribution || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        angle={selectedPeriod === "1h" ? -45 : selectedPeriod === "30d" ? -45 : 0}
                        textAnchor={selectedPeriod === "1h" || selectedPeriod === "30d" ? "end" : "middle"}
                        height={selectedPeriod === "1h" || selectedPeriod === "30d" ? 80 : 30}
                      />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="calls"
                        fill="#8884d8"
                        name="Chamadas"
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="cost"
                        fill="#82ca9d"
                        name="Custo ($)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Reports List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Relatórios Gerados</CardTitle>
                <CardDescription>
                  Histórico de relatórios e análises
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar relatórios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Relatório</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">{report.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {report.type === "api_calls" && "Chamadas de API"}
                        {report.type === "costs" && "Custos"}
                        {report.type === "performance" && "Performance"}
                      </Badge>
                    </TableCell>
                    <TableCell>{report.period}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          report.status === "generated"
                            ? "default"
                            : report.status === "generating"
                            ? "secondary"
                            : "destructive"
                        }
                      >
                        {report.status === "generated" && (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        )}
                        {report.status === "generating" && (
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        )}
                        {report.status === "error" && (
                          <AlertCircle className="h-3 w-3 mr-1" />
                        )}
                        {report.status === "generated" && "Gerado"}
                        {report.status === "generating" && "Gerando..."}
                        {report.status === "error" && "Erro"}
                      </Badge>
                    </TableCell>
                    <TableCell>{report.size}</TableCell>
                    <TableCell>
                      {format(new Date(report.createdAt), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={report.status !== "generated"}
                          onClick={() => handleDownloadReport(report)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewReport(report)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Modal de Geração de Relatório */}
        <Dialog
          open={isGenerateModalOpen}
          onOpenChange={setIsGenerateModalOpen}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Gerar Novo Relatório</DialogTitle>
              <DialogDescription>
                Configure os parâmetros para gerar um novo relatório de análise
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo de Relatório</Label>
                  <Select
                    value={reportType}
                    onValueChange={(value: any) => setReportType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="api_calls">Chamadas de API</SelectItem>
                      <SelectItem value="costs">Custos</SelectItem>
                      <SelectItem value="tokens">Tokens</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="users">Usuários</SelectItem>
                      <SelectItem value="models">Modelos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isAdminOrSupport && (
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select
                    value={selectedClient}
                    onValueChange={setSelectedClient}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Clientes</SelectItem>
                      <SelectItem value="null">Sandbox</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} {client.company ? `(${client.company})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Período</Label>
                <Select
                  value={selectedPeriod}
                  onValueChange={(value: "1h" | "24h" | "7d" | "30d") => setSelectedPeriod(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">Última Hora</SelectItem>
                    <SelectItem value="24h">Últimas 24h</SelectItem>
                    <SelectItem value="7d">Últimos 7 dias</SelectItem>
                    <SelectItem value="30d">Últimos 30 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Formato do Relatório</Label>
                <Select value={reportFormat} onValueChange={(value: "pdf" | "json") => setReportFormat(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Resumo do Relatório</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <strong>Tipo:</strong>{" "}
                    {reportType === "api_calls"
                      ? "Chamadas de API"
                      : reportType === "costs"
                      ? "Custos"
                      : reportType === "tokens"
                      ? "Tokens"
                      : reportType === "performance"
                      ? "Performance"
                      : reportType === "users"
                      ? "Usuários"
                      : "Modelos"}
                  </p>
                  <p>
                    <strong>Cliente:</strong>{" "}
                    {selectedClient === "all"
                      ? "Todos os Clientes"
                      : selectedClient}
                  </p>
                  <p>
                    <strong>Período:</strong>{" "}
                    {selectedPeriod === "1h" && "Última Hora"}
                    {selectedPeriod === "24h" && "Últimas 24h"}
                    {selectedPeriod === "7d" && "Últimos 7 dias"}
                    {selectedPeriod === "30d" && "Últimos 30 dias"}
                  </p>
                  <p>
                    <strong>Formato:</strong> PDF
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsGenerateModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Gerar Relatório
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Visualização de Relatório */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Visualizar Relatório</DialogTitle>
              <DialogDescription>{selectedReport?.name}</DialogDescription>
            </DialogHeader>

            {selectedReport && (
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-6">
                  {/* Informações do Relatório */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Tipo</Label>
                      <Badge variant="outline">
                        {selectedReport.type === "api_calls" &&
                          "Chamadas de API"}
                        {selectedReport.type === "costs" && "Custos"}
                        {selectedReport.type === "performance" && "Performance"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Período</Label>
                      <p className="text-sm text-muted-foreground">
                        {selectedReport.period}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Tamanho</Label>
                      <p className="text-sm text-muted-foreground">
                        {selectedReport.size}
                      </p>
                    </div>
                  </div>

                  {/* Conteúdo do Relatório */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-4">Conteúdo do Relatório</h4>

                    {selectedReport.type === "api_calls" && (
                      <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Total de Chamadas
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                {selectedReport.data?.kpis.totalCalls.value.toLocaleString() || "0"}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.totalCalls.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.totalCalls.variation.toFixed(1) || "0"}% vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Taxa de Sucesso
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                {selectedReport.data?.kpis.successRate.value.toFixed(1) || "0"}%
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.successRate.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.successRate.variation.toFixed(1) || "0"}% vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        <div className="h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsLineChart
                              data={selectedReport?.data?.dailyTrend || []}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="calls"
                                stroke="#8884d8"
                                name="Chamadas"
                              />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {selectedReport.type === "costs" && (
                      <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Custo Total
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                ${selectedReport.data?.kpis.totalCost.value.toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }) || "0,00"}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.totalCost.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.totalCost.variation.toFixed(1) || "0"}% vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Custo Médio por Chamada
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                ${selectedReport.data?.kpis.totalCalls.value > 0
                                  ? (selectedReport.data.kpis.totalCost.value / selectedReport.data.kpis.totalCalls.value).toFixed(3)
                                  : "0,000"}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Custo total dividido por chamadas
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        <div className="h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={selectedReport?.data?.topModels || []}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="cost"
                                fill="#82ca9d"
                                name="Custo ($)"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {selectedReport.type === "performance" && (
                      <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-3">
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Tempo Médio de Resposta
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                {selectedReport.data?.kpis.avgResponseTime.value.toFixed(1) || "0"}s
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.avgResponseTime.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.avgResponseTime.variation.toFixed(1) || "0"}s vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Taxa de Sucesso
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                {selectedReport.data?.kpis.successRate.value.toFixed(1) || "0"}%
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.successRate.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.successRate.variation.toFixed(1) || "0"}% vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">
                                Total de Chamadas
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold">
                                {selectedReport.data?.kpis.totalCalls.value.toLocaleString() || "0"}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedReport.data?.kpis.totalCalls.variation >= 0 ? "+" : ""}
                                {selectedReport.data?.kpis.totalCalls.variation.toFixed(1) || "0"}% vs período anterior
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ações */}
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setIsViewModalOpen(false)}
                    >
                      Fechar
                    </Button>
                    <Button
                      onClick={() => handleDownloadReport(selectedReport)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};
